import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { FEATURES } from "../config/features";

const Header = ({ activeSection, scrollToSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Navigation items with feature flags
  // ✅ MVP features always show
  // ❌ Additional features controlled by FEATURES config
  const allNavItems = [
    // ✅ MVP - Always visible
    { id: "home", label: "Home", enabled: FEATURES.HOME },
    { id: "about", label: "About", enabled: FEATURES.ABOUT },
    { id: "storyteller", label: "Storyteller", enabled: FEATURES.STORYTELLER },
    { id: "skills", label: "Skills", enabled: FEATURES.SKILLS },
    { id: "experience", label: "Experience", enabled: FEATURES.EXPERIENCE },
    { id: "projects", label: "Projects", enabled: FEATURES.PROJECTS },
    { id: "yahffirmations", label: "YAHFFIRMATIONS", enabled: true },
    { id: "contact", label: "Contact", enabled: FEATURES.CONTACT },
    
    // ❌ Future features - Only show in test/development
    { id: "blog", label: "INSPIYAH MAIL", enabled: FEATURES.INSPIYAH_MAIL },
    { id: "resources", label: "Resources", enabled: FEATURES.RESOURCES },
    { id: "journey", label: "INSPIYAH JOURNEY", external: false, route: "/journey", enabled: FEATURES.INSPIYAH_JOURNEY },
    { id: "shop", label: "Shop", external: false, route: "/store", enabled: FEATURES.SHOP },
  ];

  // Filter nav items based on feature flags
  const navItems = allNavItems.filter(item => item.enabled);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/90 backdrop-blur-sm shadow-sm border-b border-gray-100"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <div 
            className="cursor-pointer group flex items-center space-x-3"
            onClick={() => navigate('/')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_portfolio-ct/artifacts/3ginulsc_obey%203%20%2821%29.png"
              alt="YAH HEALTH WEALTH HAPPINESS Logo"
              className="w-16 h-16 object-contain group-hover:scale-110 transition-transform duration-300 drop-shadow-lg"
            />
            <h1 className="text-2xl font-light tracking-tight bg-gradient-to-r from-brand-deep to-fire-main bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300">
              C T.
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.external) {
                    window.open(item.url, '_blank');
                  } else if (item.route) {
                    navigate(item.route);
                  } else if (scrollToSection) {
                    scrollToSection(item.id);
                  } else {
                    navigate('/', { replace: true });
                    setTimeout(() => {
                      const element = document.getElementById(item.id);
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }
                }}
                className={`relative text-sm font-normal tracking-wide transition-all duration-300 hover:-translate-y-0.5 flex items-center space-x-1 ${
                  activeSection === item.id 
                    ? "text-brand-deep" 
                    : "text-gray-600 hover:text-fire-main"
                }`}
              >
                <span>{item.label}</span>
                {item.external && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1-2-2h6" />
                    <polyline points="15,3 21,3 21,9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                )}
                {activeSection === item.id && !item.external && !item.route && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-main to-fire-main" />
                )}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-black hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.external) {
                    window.open(item.url, '_blank');
                  } else if (item.route) {
                    navigate(item.route);
                  } else if (scrollToSection) {
                    scrollToSection(item.id);
                  } else {
                    navigate('/', { replace: true });
                    setTimeout(() => {
                      const element = document.getElementById(item.id);
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }
                  setIsMobileMenuOpen(false);
                }}
                className={`flex items-center justify-between w-full text-left px-4 py-3 text-sm font-normal tracking-wide transition-colors ${
                  activeSection === item.id && !item.external && !item.route
                    ? "text-black bg-gray-50" 
                    : "text-gray-600 hover:text-black hover:bg-gray-50"
                }`}
              >
                <span>{item.label}</span>
                {item.external && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1-2-2h6" />
                    <polyline points="15,3 21,3 21,9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;